import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useLocation, useNavigationType, useNavigate } from 'react-router-dom';
import { get } from '@/features/shared/lib/api';
import { arr, parsePayload, buildListParams } from '@/features/shared/lib/utils';
import Paginator from '@/features/shared/ui/Paginator';
import PublicationCard from '@/features/publication/components/PublicationCard';
import JournalCard from '@/features/journal/components/JournalCard';
import ResearcherCard from '@/features/researcher/components/ResearcherCard';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export default function Search() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const query = sp.get('query') || sp.get('sub_ks') || '';

  const [activeTab, setActiveTab] = useState('all'); // 'all', 'journals', 'resources', 'researchers'
  const [journalRows, setJournalRows] = useState([]);
  const [resourceRows, setResourceRows] = useState([]);
  const [researcherRows, setResearcherRows] = useState([]);

  const [journalPageInfo, setJournalPageInfo] = useState({ fromPage: 1, totalPage: 1, resultsPerPage: 20, totalResults: 0 });
  const [resourcePageInfo, setResourcePageInfo] = useState({ fromPage: 1, totalPage: 1, resultsPerPage: 20, totalResults: 0 });
  const [researcherPageInfo, setResearcherPageInfo] = useState({ fromPage: 1, totalPage: 1, resultsPerPage: 20, totalResults: 0 });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [highlightRows, setHighlightRows] = useState([]);

  const fetchHighlights = async () => {
    try {
      const data = await get('/rm/v1/highlights', { params: { posts_per_page: 5 } });
      const records = data?.records ?? [];
      setHighlightRows(Array.isArray(records) ? records : []);
    } catch (e) {
      console.error('[SEARCH][fetchHighlights]', e);
      setHighlightRows([]);
    }
  };

  const fetchJournals = async (page = 1) => {
    try {
      const params = buildListParams({
        page,
        perPage: journalPageInfo.resultsPerPage || 20,
        extra: { sub_ks: query }
      });
      const data = await get('/rm/v1/journals', { params });
      const { status, records, pageInfo: pi } = parsePayload(data);
      if (!status) throw new Error('API status=false');
      setJournalRows(records || []);
      setJournalPageInfo({
        fromPage: pi.current_page ?? pi.fromPage ?? page,
        totalPage: pi.totalPage ?? pi.total_page ?? 1,
        resultsPerPage: pi.resultsPerPage ?? pi.per_page ?? params.record_per_page ?? 20,
        totalResults: pi.totalResults ?? pi.total_results ?? 0,
      });
    } catch (e) {
      console.error('[SEARCH][fetchJournals]', e);
    }
  };

  const fetchResources = async (page = 1) => {
    try {
      const params = buildListParams({
        page,
        perPage: resourcePageInfo.resultsPerPage || 20,
        extra: { sub_ks: query }
      });
      const data = await get('/rm/v1/publications', { params });
      const { status, records, pageInfo: pi } = parsePayload(data);
      if (!status) throw new Error('API status=false');
      setResourceRows(records || []);
      setResourcePageInfo({
        fromPage: pi.current_page ?? pi.fromPage ?? page,
        totalPage: pi.totalPage ?? pi.total_page ?? 1,
        resultsPerPage: pi.resultsPerPage ?? pi.per_page ?? params.record_per_page ?? 20,
        totalResults: pi.totalResults ?? pi.total_results ?? 0,
      });
    } catch (e) {
      console.error('[SEARCH][fetchResources]', e);
    }
  };

  const fetchResearchers = async (page = 1) => {
    try {
      const params = buildListParams({
        page,
        perPage: researcherPageInfo.resultsPerPage || 21,
        extra: { sub_ks: query }
      });
      const data = await get('/rm/v1/scholars', { params });
      const { status, records, pageInfo: pi } = parsePayload(data);
      if (!status) throw new Error('API status=false');
      setResearcherRows(records || []);
      setResearcherPageInfo({
        fromPage: pi.current_page ?? pi.fromPage ?? page,
        totalPage: pi.totalPage ?? pi.total_page ?? 1,
        resultsPerPage: pi.resultsPerPage ?? pi.per_page ?? params.per_page ?? 20,
        totalResults: pi.totalResults ?? pi.total_results ?? 0,
      });
    } catch (e) {
      console.error('[SEARCH][fetchResearchers]', e);
    }
  };

  useEffect(() => {
    fetchHighlights();
  }, []);

  useEffect(() => {
    if (!query) return;
    setLoading(true);
    setErr(null);

    const fetchAll = async () => {
      try {
        await Promise.all([
          fetchJournals(1),
          fetchResources(1),
          fetchResearchers(1),
        ]);
      } catch (e) {
        setErr(e?.response?.data?.message || e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [query]);

  if (err) return <div className="p-4 text-red-600">에러: {String(err)}</div>;

  return (
    <div className="flexPage !block lg:!flex w-full mt-[127px] lg:mt-[120px] lg:px-6 xl:px-[100px]">
      <div className="flexPage !block lg:!flex w-full">
        <div className="w-full px-6 lg:px-0 2xl:px-0 pt-[30px] lg:pt-[68px]">
          <PageBreadcrumb current="Search" />

          <div className="tit-area w-full flex justify-between gap-3 mb-[35px] lg:mb-8">
            <h2 className="text-[26px] lg:text-[24px] leading-[130%] font-black">
              {query ? `All search results for '${query}'` : 'Search Results'}
            </h2>
          </div>

          {loading && (
            <div className="relative min-h-[400px]">
              <ChartSkeletonLoader show={loading} />
            </div>
          )}

          {!loading && (
            <>
              {/* Journals Section */}
              {journalRows.length > 0 && (
                <div className="w-full px-6 lg:px-8 pb-6 lg:pb-8 bg-white rounded-[12px] mb-4 lg:mb-10">
                  <div className="flex items-center py-5 lg:py-8 px-6 lg:px-0 -mx-6 lg:mx-0 border-b border-b-[#E4E8F1] lg:border-none">
                    <span className="text-18px font-bold leading-[100%]">Journal</span>
                    <span className="ml-[6px] px-2 py-[3px] text-[13px] font-bold leading-[100%] text-white bg-[#4B4DFF] rounded-full">
                      {journalPageInfo.totalResults.toLocaleString()}
                    </span>
                  </div>

                  <ul className="flex flex-col gap-6">
                    {journalRows.slice(0, 5).map((r) => (
                      <JournalCard key={r.journal_id || r.id || r.issn || Math.random()} item={r} highlightQuery={query} />
                    ))}
                  </ul>

                  {journalRows.length > 5 && (
                    <div className="mt-6 lg:mt-8 text-center">
                      <button
                        onClick={() => navigate(`/journals/?sub_ks=${encodeURIComponent(query)}`)}
                        className="px-9 py-3 text-[14px] text-[#6C7687] rounded-[12px] bg-[#F3F5F9] border border-[#E4E8F1] cursor-pointer"
                      >
                        more
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Publications Section */}
              {resourceRows.length > 0 && (
                <div className="w-full px-6 lg:px-8 pb-6 lg:pb-8 bg-white rounded-[12px] mb-4 lg:mb-10">
                  <div className="flex items-center py-5 lg:py-8 px-6 lg:px-0 -mx-6 lg:mx-0 border-b border-b-[#E4E8F1] lg:border-none">
                    <span className="text-18px font-bold leading-[100%]">Publication</span>
                    <span className="ml-[6px] px-2 py-[3px] text-[13px] font-bold leading-[100%] text-white bg-[#4B4DFF] rounded-full">
                      {resourcePageInfo.totalResults.toLocaleString()}
                    </span>
                  </div>

                  <ul className="flex flex-col gap-6">
                    {resourceRows.slice(0, 5).map((r) => (
                      <PublicationCard key={r.resource_id || r.id || r.issn || Math.random()} item={r} showAltmetric={false} highlightQuery={query} />
                    ))}
                  </ul>

                  {resourceRows.length > 5 && (
                    <div className="mt-6 lg:mt-8 text-center">
                      <button
                        onClick={() => navigate(`/publications/?sub_ks=${encodeURIComponent(query)}`)}
                        className="px-9 py-3 text-[14px] text-[#6C7687] rounded-[12px] bg-[#F3F5F9] border border-[#E4E8F1] cursor-pointer"
                      >
                        more
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* No Results */}
              {journalRows.length === 0 && resourceRows.length === 0 && researcherRows.length === 0 && (
                <div className="w-full px-6 lg:px-8 p-6 lg:pb-8 bg-white rounded-[12px]">
                  <div className="flex justify-center items-center h-[160px] text-[13px] font-semibold border border-[#E4E8F1] rounded-[12px]">
                    <div>No results found</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Researcher Sidebar */}
        <div className="right-box shrink-0 w-[calc(100%-48px)] lg:w-[310px] mt-4 lg:mt-[170px] mx-auto px-6 pb-6 lg:py-8 bg-white rounded-[12px]">
          <div className="flex items-center py-5 lg:py-0 px-6 lg:px-0 -mx-6 lg:mx-0 mb-6 lg:mb-8 border-b border-b-[#E4E8F1] lg:border-none">
            <span className="text-18px lg:text-[16px] font-bold leading-[100%]">Researcher</span>
            <span className="ml-[6px] px-2 py-[3px] text-[13px] font-bold leading-[100%] text-white bg-[#4B4DFF] rounded-full">
              {researcherPageInfo.totalResults.toLocaleString()}
            </span>
          </div>

          <ul className="flex flex-col gap-4">
            {researcherRows.length > 0 ? researcherRows.slice(0, 5).map((r) => (
              <ResearcherCard
                key={r.researcher_id || r.id || Math.random()}
                item={r}
                highlightQuery={query}
                liClassName="relative w-full px-[30px] pt-[26px] pb-4 bg-white border border-[#E4E8F1] rounded-[12px] lg:hover:shadow-[0px_4px_20px_rgba(0,0,0,0.05)] transition duration-300 cursor-pointer"
              />
            )) : (
              <li className="text-[14px] text-[#8D9AAF] py-4">No researchers</li>
            )}
          </ul>

          {researcherRows.length > 5 && (
            <div className="mt-6 lg:mt-8 text-center">
              <button
                onClick={() => navigate(`/researchers/?sub_ks=${encodeURIComponent(query)}`)}
                className="px-9 py-3 text-[14px] text-[#6C7687] rounded-[12px] bg-[#F3F5F9] border border-[#E4E8F1] cursor-pointer hover:bg-[#EEF1F8] transition-colors"
              >
                more
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
